import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Notifications from './Notifications';
import { ThemeModeContext } from '../index';

const drawerWidth = 260;

const AppLayout = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colorMode = React.useContext(ThemeModeContext);
  
  const [mobileOpen, setMobileOpen] = useState(false);

  const username = localStorage.getItem('user.username') ||
    JSON.parse(localStorage.getItem('user'))?.username ||
    'Usuario';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await axios.post('/api/auth/logout', { refreshToken })
          .catch(error => console.error('Error al revocar token:', error));
      }
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('user.username');
      localStorage.removeItem('userId');
      navigate('/login');
    }
  };

  const menuItems = [
    { num: '1', text: 'Pendientes', path: '/', icon: <ReceiptLongIcon /> },
    { num: '2', text: 'Pagadas', path: '/facturaspagadas', icon: <AssignmentTurnedInIcon /> },
    { num: '3', text: 'Proveedores', path: '/proveedores', icon: <PeopleIcon /> },
    { num: '4', text: 'Formas Pago', path: '/fdepagos', icon: <PaymentIcon /> },
    { num: '5', text: 'Informes', path: '/informes', icon: <AssessmentIcon /> },
  ];

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', px: 2, height: 64 }}>
        <ReceiptLongIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: '-0.02em' }}>
          AppVenci
        </Typography>
      </Toolbar>
      
      <Box sx={{ px: 2, py: 3, mb: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.05em' }}>
          MENÚ PRINCIPAL
        </Typography>
        <List sx={{ mt: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.num} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  selected={isActive}
                  sx={{
                    borderRadius: 1.5,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    backgroundColor: isActive ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.12)',
                      }
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive ? theme.palette.primary.main : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 600 : 500, fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid rgba(226, 232, 240, 0.8)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 1 }}>
          <Avatar 
            sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main, fontSize: '1rem', fontWeight: 600 }}
          >
            {username.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ ml: 1.5, overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }} noWrap>
              {username}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }} noWrap>
              Administrador
            </Typography>
          </Box>
        </Box>
        <ListItemButton 
          onClick={() => {
            navigate('/usuarios');
            if (isMobile) setMobileOpen(false);
          }}
          sx={{ borderRadius: 1.5, color: theme.palette.text.secondary, mb: 0.5, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Gestión Usuarios" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
        </ListItemButton>
        <ListItemButton 
          onClick={handleLogout}
          sx={{ borderRadius: 1.5, color: theme.palette.error.main, '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.08)' } }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Cerrar Sesión" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* App Bar (Header) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.8) !important',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} /> {/* Spacer */}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={theme.palette.mode === 'dark' ? "Modo Claro" : "Modo Oscuro"}>
              <IconButton onClick={colorMode.toggleColorMode} color="inherit" sx={{ p: 1 }}>
                {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Notifications />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }} // Better open performance on mobile.
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid rgba(226, 232, 240, 0.8)', backgroundColor: '#ffffff' },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          mt: '64px', // Offset for AppBar
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;
